import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, HelpCircle, BookOpen, CheckCircle2, Search, Award, Layers } from 'lucide-react';
import { QUIZ_CATEGORIES } from '../../../data/quizCategories';

export default function QuizCategories() {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredCategories = QUIZ_CATEGORIES.filter((cat) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            cat.title.toLowerCase().includes(q) ||
            cat.description.toLowerCase().includes(q) ||
            (cat.project && cat.project.toLowerCase().includes(q))
        );
    });

    const totalQuestions = QUIZ_CATEGORIES.reduce((acc, cat) => acc + cat.questions.length, 0);

    return (
        <div className="space-y-6 md:space-y-8 pb-16 max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 text-[var(--index-text-heading)]">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-[var(--index-border-color)] pb-6 md:pb-8">
                <div className="space-y-2 max-w-3xl">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="p-1.5 bg-[var(--index-primary-color)]/10 rounded-md">
                            <Sparkles className="text-[var(--index-primary-color)]" size={14} />
                        </div>
                        <span className="text-[var(--index-primary-color)] font-bold text-[10px] md:text-xs uppercase tracking-wider">
                            LGL Professional Training & Controls
                        </span>
                    </div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-[var(--index-text-heading)]">
                        General Quiz & Knowledge Bank
                    </h1>
                    <p className="text-[var(--index-text-secondary)] text-xs sm:text-sm md:text-base font-medium leading-relaxed">
                        Comprehensive curriculum evaluations covering 600 verified industry questions across all 6 core project controls and scheduling modules. Practice with randomized diagnostic quizzes or study full module question banks.
                    </p>
                </div>

                {/* Search Bar */}
                <div className="relative w-full md:w-80 shrink-0">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--index-text-secondary)]" size={16} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search modules or topics..."
                        className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-[var(--index-card-bg)] border border-[var(--index-border-color)] text-xs md:text-sm font-medium text-[var(--index-text-heading)] placeholder:text-[var(--index-text-secondary)]/60 focus:outline-none focus:border-[var(--index-primary-color)] transition-all"
                    />
                </div>
            </header>

            {/* Quick Metrics Banner */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-teal-500/10 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0">
                        <Layers size={20} />
                    </div>
                    <div>
                        <div className="text-base md:text-xl font-black">{QUIZ_CATEGORIES.length} Modules</div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">Complete Core Track</div>
                    </div>
                </div>

                <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                        <HelpCircle size={20} />
                    </div>
                    <div>
                        <div className="text-base md:text-xl font-black">{totalQuestions} Questions</div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">100 Per Module</div>
                    </div>
                </div>

                <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                        <Award size={20} />
                    </div>
                    <div>
                        <div className="text-base md:text-xl font-black">70% Pass Mark</div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">Diagnostic Standard</div>
                    </div>
                </div>

                <div className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                        <BookOpen size={20} />
                    </div>
                    <div>
                        <div className="text-base md:text-xl font-black">Dual Mode</div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-[var(--index-text-secondary)]">Quiz & Study Bank</div>
                    </div>
                </div>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredCategories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                        <div
                            key={cat.id}
                            className="bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[28px] p-6 flex flex-col justify-between hover:shadow-lg transition-all duration-300 group hover:border-[var(--index-primary-color)]/40 relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-transparent to-current opacity-[0.03] rounded-bl-full pointer-events-none" style={{ color: cat.color }} />

                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div
                                        className="w-13 h-13 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105 duration-300"
                                        style={{ backgroundColor: cat.color + '15' }}
                                    >
                                        <Icon size={26} color={cat.color} />
                                    </div>
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border"
                                        style={{
                                            backgroundColor: cat.color + '10',
                                            borderColor: cat.color + '30',
                                            color: cat.color
                                        }}
                                    >
                                        <CheckCircle2 size={12} /> {cat.questions.length} Q&A
                                    </span>
                                </div>

                                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--index-text-secondary)]/80 mb-1">
                                    {cat.project || 'Capital Controls Project'}
                                </div>

                                <h3 className="text-lg md:text-xl font-bold text-[var(--index-text-heading)] tracking-tight leading-snug mb-2 group-hover:text-[var(--index-primary-color)] transition-colors">
                                    {cat.title}
                                </h3>

                                <p className="text-[var(--index-text-secondary)] text-xs md:text-sm font-medium leading-relaxed mb-6 line-clamp-3">
                                    {cat.description}
                                </p>
                            </div>

                            <div className="flex items-center gap-2 pt-4 border-t border-[var(--index-border-color)]/60">
                                <Link
                                    to={`/student/quiz/${cat.id}?mode=quiz`}
                                    className="flex-1 py-2.5 px-4 rounded-xl text-xs font-black uppercase tracking-wider text-white flex items-center justify-center gap-1.5 hover:opacity-90 active:scale-95 transition-all no-underline shadow-sm"
                                    style={{ backgroundColor: cat.color }}
                                >
                                    Take Quiz <ArrowRight size={14} />
                                </Link>
                                <Link
                                    to={`/student/quiz/${cat.id}?mode=study`}
                                    className="py-2.5 px-4 rounded-xl text-xs font-bold uppercase tracking-wider border border-[var(--index-border-color)] text-[var(--index-text-secondary)] hover:bg-[var(--index-hover-bg)] hover:text-[var(--index-text-heading)] transition-all no-underline flex items-center gap-1.5"
                                >
                                    <BookOpen size={14} /> Study All
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>

            {filteredCategories.length === 0 && (
                <div className="text-center py-16 bg-[var(--index-card-bg)] border border-[var(--index-border-color)] rounded-[32px] space-y-3">
                    <Search className="mx-auto text-[var(--index-text-secondary)]" size={32} />
                    <p className="font-bold text-base">No modules match "{searchQuery}"</p>
                    <button
                        onClick={() => setSearchQuery('')}
                        className="text-xs font-bold text-[var(--index-primary-color)] uppercase tracking-wider hover:underline"
                    >
                        Clear Filter
                    </button>
                </div>
            )}
        </div>
    );
}
